import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Plus, FileText, RefreshCw, Filter, Search, ShoppingCart, FileQuestion, Package, Receipt, ClipboardList, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const P2PDocumentTypes = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('p2p_document_type');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    document_type_id: '',
    description: '',
    category: '',
    number_range_prefix: '',
    is_active: true,
    requires_approval: false,
    auto_po_creation: false,
    goods_receipt_required: true,
    invoice_receipt_required: true,
    item_category_default: '',
    notes: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, categoriesRes] = await Promise.all([
        axios.get(`${backendUrl}/api/p2p/document-types/`),
        axios.get(`${backendUrl}/api/p2p/document-types/categories`)
      ]);
      setDocumentTypes(typesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load document types');
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const seedDefaults = async () => {
    try {
      await axios.post(`${backendUrl}/api/p2p/document-types/seed`);
      toast.success('Default document types seeded successfully');
      loadData();
    } catch (error) {
      console.error('Error seeding defaults:', error);
      toast.error('Failed to seed default document types');
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setSelectedType(null);
    setFormData({
      document_type_id: '',
      description: '',
      category: '',
      number_range_prefix: '',
      is_active: true,
      requires_approval: false,
      auto_po_creation: false,
      goods_receipt_required: true,
      invoice_receipt_required: true,
      item_category_default: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleEdit = (docType) => {
    setEditMode(true);
    setSelectedType(docType);
    setFormData({
      document_type_id: docType.document_type_id,
      description: docType.description,
      category: docType.category,
      number_range_prefix: docType.number_range_prefix || '',
      is_active: docType.is_active,
      requires_approval: docType.requires_approval,
      auto_po_creation: docType.auto_po_creation,
      goods_receipt_required: docType.goods_receipt_required,
      invoice_receipt_required: docType.invoice_receipt_required,
      item_category_default: docType.item_category_default || '',
      notes: docType.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await axios.put(
          `${backendUrl}/api/p2p/document-types/${selectedType.document_type_id}`,
          formData
        );
        toast.success('Document type updated successfully');
      } else {
        await axios.post(`${backendUrl}/api/p2p/document-types/`, formData);
        toast.success('Document type created successfully');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving document type:', error);
      toast.error(error.response?.data?.detail || 'Failed to save document type');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'PR': <ClipboardList className="w-4 h-4" />,
      'RFQ': <FileQuestion className="w-4 h-4" />,
      'PO': <ShoppingCart className="w-4 h-4" />,
      'GR': <Package className="w-4 h-4" />,
      'IR': <Receipt className="w-4 h-4" />,
      'SES': <FileText className="w-4 h-4" />,
      'RET': <RotateCcw className="w-4 h-4" />
    };
    return icons[category] || <FileText className="w-4 h-4" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'PR': 'bg-orange-100 text-orange-700',
      'RFQ': 'bg-orange-100 text-orange-700',
      'PO': 'bg-green-100 text-green-700',
      'GR': 'bg-orange-100 text-orange-700',
      'IR': 'bg-pink-100 text-pink-700',
      'SES': 'bg-orange-100 text-orange-700',
      'RET': 'bg-red-100 text-red-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'PR': 'Purchase Requisition',
      'RFQ': 'Request for Quotation',
      'PO': 'Purchase Order',
      'GR': 'Goods Receipt',
      'IR': 'Invoice Receipt',
      'SES': 'Service Entry',
      'RET': 'Returns'
    };
    return labels[category] || category;
  };

  const filteredTypes = documentTypes.filter(dt => {
    const matchesCategory = filterCategory === 'all' || dt.category === filterCategory;
    const matchesSearch = searchQuery === '' || 
      dt.document_type_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dt.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group by category for display
  const groupedTypes = filteredTypes.reduce((acc, dt) => {
    if (!acc[dt.category]) {
      acc[dt.category] = [];
    }
    acc[dt.category].push(dt);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="p2p-document-types-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">P2P Document Types</h1>
            <p className="text-gray-500 mt-1">Configure document types for Procure-to-Pay processes</p>
          </div>
          <div className="flex items-center gap-3">
            {writable && (
            <Button 
              variant="outline" 
              onClick={seedDefaults}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Seed Defaults
            </Button>
            )}
            {writable && (
            <Button 
              onClick={handleCreate}
              className="flex items-center gap-2 bg-stone-800 hover:bg-stone-700"
            >
              <Plus className="w-4 h-4" />
              New Document Type
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter:</span>
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by ID or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="text-sm text-gray-500">
                {filteredTypes.length} document type(s)
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document Types Table */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-500">Loading document types...</p>
            </CardContent>
          </Card>
        ) : filteredTypes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No document types found</h3>
              <p className="mt-2 text-gray-500">
                {documentTypes.length === 0 
                  ? 'Click "Seed Defaults" to create standard document types or create a new one.'
                  : 'Try adjusting your filters.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Type ID</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead className="text-center">Approval</TableHead>
                  <TableHead className="text-center">GR Req.</TableHead>
                  <TableHead className="text-center">IR Req.</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((docType) => (
                  <TableRow key={`${docType.category}-${docType.document_type_id}`}>
                    <TableCell className="font-mono font-medium">{docType.document_type_id}</TableCell>
                    <TableCell>{docType.description}</TableCell>
                    <TableCell>
                      <Badge className={`${getCategoryColor(docType.category)} flex items-center gap-1 w-fit`}>
                        {getCategoryIcon(docType.category)}
                        {docType.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-gray-600">
                      {docType.number_range_prefix || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {docType.requires_approval ? (
                        <Badge className="bg-amber-100 text-amber-700">Yes</Badge>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {docType.goods_receipt_required ? (
                        <Badge className="bg-green-100 text-green-700">Yes</Badge>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {docType.invoice_receipt_required ? (
                        <Badge className="bg-orange-100 text-orange-700">Yes</Badge>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={docType.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                        {docType.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <RecordActions
                        doctype="p2p_document_type"
                        idForTestId={docType.document_type_id}
                        onView={() => setViewItem(docType)}
                        onEdit={() => handleEdit(docType)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Category Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {categories.map(cat => {
            const count = documentTypes.filter(dt => dt.category === cat.value).length;
            return (
              <Card 
                key={cat.value} 
                className={`cursor-pointer transition-all hover:shadow-md ${filterCategory === cat.value ? 'ring-2 ring-stone-400' : ''}`}
                onClick={() => setFilterCategory(filterCategory === cat.value ? 'all' : cat.value)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center ${getCategoryColor(cat.value)}`}>
                    {getCategoryIcon(cat.value)}
                  </div>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-xs text-gray-500">{cat.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Edit Document Type' : 'Create New Document Type'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="document_type_id">Document Type ID *</Label>
                <Input
                  id="document_type_id"
                  value={formData.document_type_id}
                  onChange={(e) => setFormData({...formData, document_type_id: e.target.value.toUpperCase()})}
                  placeholder="e.g., NB, FO, UB"
                  maxLength={4}
                  disabled={editMode}
                  className="font-mono"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({...formData, category: value})}
                  disabled={editMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="e.g., Standard Purchase Order"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="number_range_prefix">Number Range Prefix</Label>
                <Input
                  id="number_range_prefix"
                  value={formData.number_range_prefix}
                  onChange={(e) => setFormData({...formData, number_range_prefix: e.target.value.toUpperCase()})}
                  placeholder="e.g., PO, GR"
                  maxLength={10}
                  className="font-mono"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="item_category_default">Default Item Category</Label>
                <Input
                  id="item_category_default"
                  value={formData.item_category_default}
                  onChange={(e) => setFormData({...formData, item_category_default: e.target.value})}
                  placeholder="e.g., STANDARD"
                />
              </div>
              
              <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label htmlFor="is_active" className="text-sm">Active</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requires_approval"
                    checked={formData.requires_approval}
                    onCheckedChange={(checked) => setFormData({...formData, requires_approval: checked})}
                  />
                  <Label htmlFor="requires_approval" className="text-sm">Requires Approval</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="goods_receipt_required"
                    checked={formData.goods_receipt_required}
                    onCheckedChange={(checked) => setFormData({...formData, goods_receipt_required: checked})}
                  />
                  <Label htmlFor="goods_receipt_required" className="text-sm">GR Required</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="invoice_receipt_required"
                    checked={formData.invoice_receipt_required}
                    onCheckedChange={(checked) => setFormData({...formData, invoice_receipt_required: checked})}
                  />
                  <Label htmlFor="invoice_receipt_required" className="text-sm">IR Required</Label>
                </div>
              </div>

              {formData.category === 'PR' && (
                <div className="col-span-2 flex items-center space-x-2 pt-2 border-t">
                  <Checkbox
                    id="auto_po_creation"
                    checked={formData.auto_po_creation}
                    onCheckedChange={(checked) => setFormData({...formData, auto_po_creation: checked})}
                  />
                  <Label htmlFor="auto_po_creation" className="text-sm">Auto-create PO when approved (PR only)</Label>
                </div>
              )}
              
              <div className="space-y-2 col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about this document type..."
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!formData.document_type_id || !formData.description || !formData.category}
                className="bg-stone-800 hover:bg-stone-700"
              >
                {editMode ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RecordViewDialog
          open={!!viewItem}
          onOpenChange={(o) => { if (!o) setViewItem(null); }}
          title={`Document Type ${viewItem?.document_type_id || ''}`}
          record={viewItem}
        />
      </div>
    </Layout>
  );
};

export default P2PDocumentTypes;
